import { useLocalStorage } from "@mantine/hooks";
import { UserReduxMetadataSchema } from "@tsTypes/next-auth";
import { runLogoutFunctions } from "@utils/accessChecks";
import { Session } from "next-auth/core/types";
import { useSession } from "next-auth/react";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { setCustomTestState } from "../../features/progress/customTestUserReduxMetadataSlice";
import { setMockExamState } from "../../features/progress/mockExamUserReduxMetadataSlice";
import { setSubTopicState } from "../../features/progress/subTopicUserReduxMetadataSlice";
import { setTopicTestState } from "../../features/progress/topicTestUserReduxMetadataSlice";
import { setSyllabusState } from "../../features/syllabus/subjectSyllabusChecklistSlice";
import { extractExpiryTime } from "../../lib/nextAuth/formatters";
import {
  getUserProgressDataFromDB,
  saveUserProgressToDB,
} from "../../lib/redux/helpers";
import {
  useCustomDispatch,
  useUserReduxProgressMetadataSelector,
} from "../../lib/redux/hooks";
import { setNotification } from "./notifications";

export default function RefreshTokenHandler({
  setAuthInterval,
}: {
  setAuthInterval: Dispatch<SetStateAction<number>>;
}) {
  const [timeSinceSave, setTimeSinceSave] = useLocalStorage<number>({
    key: "timeSinceProgressSave",
    defaultValue: 0,
  });
  const { data: session } = useSession();
  const dispatch = useCustomDispatch();
  const userReduxProgressMetadata = useUserReduxProgressMetadataSelector();
  const isSaving = useRef(false);

  function updateAllUserReduxProgressMetadataState(
    meta: UserReduxMetadataSchema
  ) {
    dispatch(setSubTopicState(meta.SubtopicUserReduxMetadata));
    dispatch(setTopicTestState(meta.TopicTestUserReduxMetadata));
    dispatch(setCustomTestState(meta.CustomTestUserReduxMetadata));
    dispatch(setMockExamState(meta.MockExamUserReduxMetadata));
    dispatch(setSyllabusState(meta.SubjectSyllabus));
  }

  async function getProgressData(session: Session) {
    const userProgressData = await getUserProgressDataFromDB(
      session!.user.documentId
    );
    updateAllUserReduxProgressMetadataState(userProgressData.data);
    setTimeSinceSave(0);
    localStorage.setItem(
      "progressMeta",
      JSON.stringify({
        key: userProgressData.key,
        documentId: userProgressData.documentId,
      })
    );
  }

  async function saveProgressData(session: Session) {
    try {
      const localStorageProgressMeta: { key: string; documentId: string } =
        JSON.parse(localStorage.getItem("progressMeta") || "{}");
      // if we dont have a key or documentId, then throw error
      if (
        !localStorageProgressMeta.documentId ||
        !localStorageProgressMeta.key
      ) {
        throw new Error();
      }
      // save the progress data to the database
      const { key, documentId, data, error } = await saveUserProgressToDB(
        session.user.documentId,
        localStorageProgressMeta.documentId,
        localStorageProgressMeta.key,
        userReduxProgressMetadata
      );
      // update the redux state (unchanged if all went ok but we write back to make this function easy to understand)
      updateAllUserReduxProgressMetadataState(data);
      // update the local store of the key and documentId
      localStorage.setItem("progressMeta", JSON.stringify({ key, documentId }));
      // set time since save to 0 so we dont save again for 10 minutes
      setTimeSinceSave(0);

      // if we returned any controlled errors (e.g. mismatched key or admin changed), then show that as a notification
      // we still want the above logic to run as we override the progress and timeSinceSave since this error is not functional but for informing the user
      if (error) {
        setNotification("Progress", false, error, "error");
      }
    } catch (error) {
      console.error(error);
      setNotification(
        "Progress",
        false,
        (error as { msg: string }).msg === "fetch failed"
          ? "Saving your device progress failed, the system will re-try shortly. Leave this window open and ensure you are connected to the internet."
          : (error as { msg: string }).msg,
        "error"
      );
    }
  }
  // **************************************************************************** Background Clock to keep track of time since save
  // runs every 60s and counts up minutes by 1
  useEffect(() => {
    const int = setInterval(() => {
      setTimeSinceSave((prev) => prev + 1);
    }, 60000);
    return () => {
      setTimeSinceSave(0);
      clearInterval(int);
    };
  }, []);
  // **************************************************************************** REDUX logic to save state after time interval
  // saves data if time since save is 10 minutes or more (when idling and not triggered)
  useEffect(() => {
    const saveTrigger =
      Number(process.env.NEXT_PUBLIC_SAVE_PROGRESS_TRIGGER) || 10;
    if (!session || timeSinceSave < saveTrigger || isSaving.current) {
      return;
    }
    const saveProgress = async (s: Session) => {
      await saveProgressData(s);
    };
    isSaving.current = true;
    saveProgress(session).finally(() => {
      isSaving.current = false;
    });
  }, [timeSinceSave]);
  // **************************************************************************** Refresh token logic
  useEffect(() => {
    async function sortSignOut() {
      if (!session) {
        return;
      }
      await runLogoutFunctions(
        dispatch,
        session.user.documentId,
        userReduxProgressMetadata
      );
    }

    if (!!session) {
      if (session.error) {
        void (async () => {
          await sortSignOut();
        })();
      } else {
        const refetchTrigger =
          (Number(process.env.NEXT_PUBLIC_ACCESS_TOKEN_TRIGGER) || 5) *
          60 *
          1000;
        const expiryTime = extractExpiryTime(session.accessToken);
        const timeToRefetch = Math.round(
          (expiryTime - refetchTrigger - Date.now()) / 1000
        );
        const next = timeToRefetch > 0 ? timeToRefetch : 0;
        setAuthInterval((prev) => (prev === next ? prev : next));
      }

      if (!localStorage.getItem("progressMeta")) {
        getProgressData(session).catch((err) => {
          setNotification(
            "Progress",
            false,
            (err as { msg?: string })?.msg ?? "Failed to load progress",
            "error"
          );
        });
      }
    }
  }, [session]);
  return null;
}
